import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IWindowMessage } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { AuthReloadEvent } from 'app/um/lib/events';
import { CaseService } from 'app/cm/services/case-service';
import { CampaignService } from 'app/pm/services/campaign-service';
import { TransitionalManager } from 'app/shared/lib/transitional/manager'
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { getAbsoluteUrl } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as PM from 'app/pm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';


@autoinject()
export class AuthRefer extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public auth: UM.Auth | null = null;

    protected member: CD.Member | null = null;
    protected cases: CM.Case[] | null = null;
    protected candidates: PM.Candidate[] | null = null;
    protected showCM: boolean = false;
    protected showPM: boolean = false;
    protected hasCaseRoute = false;
    protected showCaseSummaryLink: boolean = false;

    private authReloaded: Subscription | null = null;

    protected openCaseSummary = openCaseSummary;

    private listener: (event: any) => {};


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AuthPageContext,
        private readonly events: EventAggregator,
        private readonly caseService: CaseService,
        private readonly campaignService: CampaignService,
        private readonly transitional: TransitionalManager,
        ...rest: any[]) {

        super(...rest);

        this.listener = this.handleTransitionalMessage.bind(this);
    }

    protected bind(): void {
        if(!(this.auth || this.context.auth)) { throw new Error('No auth information was bound'); }
        if(!this.auth) {
            this.auth = this.context.auth;
        }

        this.member = this.auth.member || null;
        this.reload();

        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.auth = this.context.auth;
            this.member = this.auth.member || null;

            this.reload();
        });
    }
    protected detached(): void {
        if(this.authReloaded) {
            this.authReloaded.dispose();
        }

        window.removeEventListener('message', this.listener, false);
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        if(this.env.config.modules.includes('cm')) {
            this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
            this.showCM = this.accountContext.hasPermission('UM.Auth.Query.ViewCaseManagementStatus');
            this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        }
        if(this.env.config.modules.includes('pm')) {
            this.showPM = this.accountContext.hasPermission('UM.Auth.Query.ViewPMCampaignStatus');
        }
    }
    public async reload(): Promise<void> {
        await this.checkPermissions();
        if(this.member && !this.member.isUnknown) {
            if(this.showCM) { this.loadCases(this.member.id!); }
            if(this.showPM) { this.loadCandidates(this.member.id!) }
        }
    }
    protected openCaseCreate(): void {
        if(!(this.member && this.member.id)) { return; }

        const url = getAbsoluteUrl(this.env.resolveUrl(`~/#/cm/manage-cases/create/${this.member.id}/?origin=UM.Auth`));
        this.router.navigate(url);
    }

    protected openAddToCampaign(): void {
        if(!this.member?.id) { return; }

        const transitionalIds: string[] = [this.member.id!, 'UM.Auth'];
        this.transitional.openTransitional(null, '14083', transitionalIds);

        window.addEventListener('message', this.listener, false);
    }

    private async handleTransitionalMessage(event: MessageEvent): Promise<void> {
        if(!this.transitional.checkOrigin(event.origin)) {
            this.logger.error(`Transitional message event origin was not allowed: ${event.origin}`);
            return;
        }

        const message: IWindowMessage = event.data ?? null;
        if(!message) {
            this.logger.error(`Transitional message event did not contain data`);
            return;
        }

        if(message.action === 'REFRESH' && !!this.member?.id) {
            this.loadCandidates(this.member.id);
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadCases(memberId: string): Promise<void> {
        const includes = [
            'assignedTo',
            'status'
        ];
        const fields = [
            'assignedTo.name.display',
            'member.id',
            'number',
            'status.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.caseService.searchCases({}, { id: memberId }, { all: true }, includes, fields);
            this.cases = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadCandidates(memberId: string): Promise<void> {
        const includes = [
            'assignedTo',
            'campaign',
            'status'
        ];

        const fields = [
            'assignedTo.description',
            'campaign.description',
            'number',
            'status.description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.campaignService.loadCandidatesByMemberId(memberId, { all: true }, includes, fields);
            this.candidates = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
